<template>
  <div class="service-content-detail">
    <BaseSkeleton :loading="pending" type="default" :rows="8">
      <template v-if="contentDetail">
        <div class="base-container">
          <HelpBreadcrumb
            :center-label="centerLabel"
            :current="breadcrumbCurrent"
            :list-route="listRoute"
          />
          <HelpHeader
            :title="title"
            :applicable-products="applicableProducts"
            :show-share="showShare"
            :share-url="shareUrl"
            :share-link-label="shareLinkLabel"
            @share="emit('share')"
          >
            <template v-if="$slots.subtitle" #subtitle>
              <slot name="subtitle" />
            </template>
          </HelpHeader>

          <div class="service-content-detail__body" :class="{ 'has-toc': tocItems.length > 0 }">
            <div class="service-content-detail__main-wrapper">
              <div class="service-content-detail__main">
                <ContentResourcePreview
                  v-if="detailResource.hasResource"
                  :resource="detailResource"
                  :downloading="isResourceDownloading"
                  @download="emit('download', $event)"
                  @refresh-required="emit('refresh-required', $event)"
                />
                <BaseRichTextPreview
                  v-if="processedHtml"
                  profile="tdc"
                  :html-content="processedHtml"
                  class="service-content-detail__rich-text"
                />
                <div
                  v-else-if="!detailResource.hasResource"
                  class="service-content-detail__empty-content"
                >
                  暂无内容
                </div>
              </div>

              <HelpFileList
                :files="files"
                :downloading-map="downloadingMap"
                :resolve-preview-file="resolvePreviewFile"
                @preview="emit('preview', $event)"
                @download="emit('download', $event)"
                @share="emit('file-share', $event)"
              />
              <Feedback class="service-content-detail__feedback" :content-id="contentId" />
            </div>

            <div v-if="tocItems.length > 0" class="service-content-detail__toc-wrapper">
              <HelpTableOfContents
                :items="tocItems"
                :affix-offset-top="100"
                @click="emit('toc-click', $event)"
              />
            </div>
          </div>
        </div>
      </template>

      <slot v-else-if="error" name="error">
        <a-result status="error" :title="errorTitle">
          <template #extra>
            <a-button type="primary" @click="emit('refresh-required')">重试</a-button>
          </template>
        </a-result>
      </slot>
    </BaseSkeleton>
  </div>
</template>

<script setup>
/**
 * @description 服务中心公共详情视图。帮助中心作为布局基线，业务页面只提供面包屑参数和副标题。
 */
import HelpBreadcrumb from '~/components/website/service/help/Breadcrumb.vue'
import HelpHeader from '~/components/website/service/help/Header.vue'
import HelpFileList from '~/components/website/service/help/FileList.vue'
import HelpTableOfContents from '~/components/website/service/help/TableOfContents.vue'
import ContentResourcePreview from '~/components/website/service/ContentResourcePreview.vue'
import Feedback from '~/components/website/service/Feedback.vue'

defineProps({
  pending: { type: Boolean, default: false },
  error: { type: [Object, Boolean], default: null },
  errorTitle: { type: String, default: '内容加载失败' },
  contentDetail: { type: Object, default: null },
  title: { type: String, default: '' },
  centerLabel: { type: String, default: '帮助中心' },
  breadcrumbCurrent: { type: String, default: '内容详情' },
  listRoute: { type: [String, Object], default: '/website/service/help' },
  applicableProducts: { type: Array, default: () => [] },
  showShare: { type: Boolean, default: false },
  shareUrl: { type: String, default: '' },
  shareLinkLabel: { type: String, default: '内容详情链接' },
  detailResource: { type: Object, default: () => ({ hasResource: false }) },
  processedHtml: { type: String, default: '' },
  files: { type: Array, default: () => [] },
  downloadingMap: { type: Object, default: () => ({}) },
  resolvePreviewFile: { type: Function, default: null },
  isResourceDownloading: { type: Boolean, default: false },
  contentId: { type: [String, Number], default: '' },
  tocItems: { type: Array, default: () => [] },
})

const emit = defineEmits([
  'share',
  'download',
  'refresh-required',
  'preview',
  'file-share',
  'toc-click',
])
</script>

<style lang="scss" scoped>
.service-content-detail {
  width: 100%;
  height: auto;
  overflow: visible;
  padding: 20px 0 32px;
  background-color: $bg-white;

  @include respond-to-max('lg') {
    height: auto;
    min-height: calc(100vh - 48px);
    overflow: visible;
    padding: 12px 0 0;
  }

  &__body {
    display: grid;
    grid-template-columns: 1fr;
    align-items: start;

    &.has-toc {
      grid-template-columns: minmax(0, 1120px) 280px;
    }

    @include respond-to-max('xl') {
      gap: 32px;

      &.has-toc {
        grid-template-columns: 1fr;
      }
    }

    @include respond-to-max('lg') {
      display: block;
    }
  }

  &__main-wrapper {
    display: flex;
    width: 100%;
    min-width: 0;
    flex-direction: column;
  }

  &__main {
    min-width: 0;
    padding-top: 32px;

    :deep([id]) {
      scroll-margin-top: $base-header-height;
    }

    @include respond-to-max('lg') {
      padding-top: 16px;
    }
  }

  &__rich-text {
    margin-top: 24px;

    @include respond-to-max('lg') {
      margin-top: 16px;
    }
  }

  &__toc-wrapper {
    @include respond-to-max('xl') {
      display: flex;
      width: 100%;
      justify-content: flex-end;
    }

    @include respond-to-max('lg') {
      display: none;
    }
  }

  &__empty-content {
    display: flex;
    min-height: 160px;
    align-items: center;
    justify-content: center;
    border: 1px solid $border-light-color;
    border-radius: $radius-6;
    color: $text-subtitle-color;
    font-size: 14px;
    line-height: 22px;
  }

  &__feedback {
    margin: 16px 0;
  }
}
</style>

<style lang="scss">
body:has(.service-content-detail) {
  .website-layout__main {
    background-color: $bg-white;
  }

  .base-footer {
    border-top: 1px solid #e5e6eb;
  }

  .customer-service-bot,
  .customer-service-bot-mobile,
  .back-to-top {
    display: none !important;
  }

  @include respond-to-max('lg') {
    footer {
      display: none !important;
    }
  }
}
</style>
